import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ProductEvent } from "@onsemble/shared";
import {
  detectFractionRewrites,
  applyFractionRewrites,
} from "@/utils/fraction-detection";

/** How long the input must sit still before a transform is attempted. */
const TRANSFORM_DEBOUNCE_MS = 500;
/** Shortest text a transform will run on, ignoring surrounding spaces. */
const MIN_TRANSFORM_LENGTH = 2;

/**
 * Where the word the user may still be typing begins.
 *
 * A space finishes the word before it, so the unfinished word starts just
 * after the last space.
 */
function unfinishedWordStart(text: string): number {
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    // Each space moves the start of the unfinished word past it.
    if (/\s/.test(text[i])) start = i + 1;
  }
  return start;
}

/**
 * The fraction rewrite for `value`, or null when none applies.
 *
 * `holdUnfinishedWord` holds back any rewrite whose span reaches into the word
 * the user may still be typing, and applies the rest: in "12 x 12" the first
 * size is finished and becomes "1/2", while the second waits, so the box reads
 * "1/2 x 12" until a space or a committed search finishes it. A committed
 * search passes false, since pressing search finishes the word.
 */
function detectFractionTransform(
  value: string,
  holdUnfinishedWord: boolean,
): string | null {
  if (value.trim().length < MIN_TRANSFORM_LENGTH) return null;

  const { trimmed, rewrites } = detectFractionRewrites(value);

  // Where the unfinished word starts. Text ending in a space has none: that
  // space is what finished the word before it.
  const unfinishedFrom = /\s$/.test(value)
    ? trimmed.length
    : unfinishedWordStart(trimmed);

  const applicable = holdUnfinishedWord
    ? rewrites.filter((r) => r.index + r.length <= unfinishedFrom)
    : rewrites;
  if (applicable.length === 0) return null;

  const rewritten = applyFractionRewrites(trimmed, applicable);
  if (rewritten === trimmed) return null;

  // Put the surrounding whitespace back around the rewrite: the detector works
  // on trimmed text, and dropping the space the user just typed would land the
  // next keystroke against the fraction ("1/2copper").
  const leading = value.slice(0, value.length - value.trimStart().length);
  const trailing = value.slice(value.trimEnd().length);
  return leading + rewritten + trailing;
}

/** A rewrite currently sitting in the box in place of the user's own text. */
interface AppliedRewrite {
  /** What the user had actually typed when the rewrite was written. */
  typed: string;
  /** What was put in the box in its place. */
  transformed: string;
}

/**
 * Recovers the user's own text from a box that may hold a rewrite.
 *
 * Every rewrite is provisional: "1 5 " shows as "1/5 ", but the next keystroke
 * has to be read against "1 5 ", not against "1/5 ", or "1 5 8" can never be
 * recognised as the mixed number "1-5/8" — the "8" would land on a fraction
 * that was only ever a guess. Appending to a rewrite is mapped back onto the
 * typed text; any other edit (a deletion, a change mid-string) abandons the
 * mapping and the box is taken at face value.
 *
 * Because detection always reads this text and never its own output, the
 * result depends on what the user typed, not on how fast they typed it.
 */
function typedTextFor(
  boxValue: string,
  applied: AppliedRewrite | null,
): string {
  if (!applied) return boxValue;
  if (boxValue.startsWith(applied.transformed)) {
    return applied.typed + boxValue.slice(applied.transformed.length);
  }
  return boxValue;
}

/**
 * Whether the box still holds the text a revert put back.
 *
 * A revert has to keep the transform away for as long as the user is working
 * on the text they kept: someone who kept "34 pipe" and types " 2" onto it
 * still means it, and so does someone who backspaces to "34 pip". Replacing
 * the text outright — select-all and retype, or a paste over the top — is a
 * different query, and one the transform is free to read again. Growing and
 * trimming both leave one string a prefix of the other, which is the test.
 *
 * Exported because the server-correction revert in the catalog search box
 * latches the same way and has to last exactly as long.
 */
export function holdsRevertedText(
  boxValue: string,
  revertedText: string | null,
): boolean {
  if (revertedText === null) return false;
  return boxValue.startsWith(revertedText) || revertedText.startsWith(boxValue);
}

interface UseFractionTransformOptions {
  /**
   * Writes text into whatever state backs the search box — the rewrite, or the
   * user's own text again when a provisional rewrite no longer holds.
   */
  onTransform: (transformed: string) => void;
  /** Puts the typed text back and re-runs the caller's search. */
  onRevert: (original: string) => void;
  /** Sends the transform and revert analytics events. */
  capture: (event: ProductEvent) => void;
}

interface UseFractionTransformResult {
  /** Text the box held before the transform, or null when nothing is showing. */
  original: string | null;
  /** Call on every keystroke; schedules the debounced transform. */
  handleInputChange: (newValue: string) => void;
  /**
   * Call when the user commits the search. Applies a rewrite the debounce was
   * holding back off the unfinished word, and returns the text to search.
   */
  commit: (currentValue: string) => string;
  /** Restores the typed text and stops the transform coming back. */
  revert: () => void;
  /**
   * Starts the box over: drops any pending transform, clears the banner and
   * lets rewrites happen again after a revert. Callers use this wherever the
   * text is replaced wholesale — cleared, or swapped for a suggestion — which
   * is the same fresh start as backspacing the box to empty.
   */
  reset: () => void;
}

/**
 * Rewrites fraction shorthand in a search box after the user stops typing,
 * keeping the text they typed so it can be put back.
 *
 * Two forms are recognised, no-space first: "12 copper" becomes "1/2 copper",
 * and "1 1 2" becomes "1-1/2".
 *
 * A rewrite of the word still being typed waits for the user to finish it,
 * either by typing a space or by committing the search through `commit`.
 *
 * Every rewrite is provisional. The typed text is kept and detection always
 * re-runs against it, so a guess can be revised as the query grows: "1 5 "
 * shows as "1/5 ", and once "8 pipe" follows it becomes "1-5/8 pipe" rather
 * than the "1/5/8 pipe" that rewriting a rewrite would give.
 */
export function useFractionTransform({
  onTransform,
  onRevert,
  capture,
}: UseFractionTransformOptions): UseFractionTransformResult {
  // Pre-transform text, which the caller renders as a revert banner.
  const [original, setOriginal] = useState<string | null>(null);
  // The text a revert put back, latching the transform off while the box
  // still holds it. Null once the user has moved on to a different query.
  const [revertedText, setRevertedText] = useState<string | null>(null);
  // The pending debounce, cleared whenever a new keystroke restarts it.
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Id of the most recent transform, read back when reverting.
  const transformationIdRef = useRef<string | null>(null);
  // The rewrite currently in the box, and the typed text it stands in for.
  const appliedRef = useRef<AppliedRewrite | null>(null);
  // Latest callbacks, so the returned functions keep a stable identity even
  // though callers pass fresh closures on every render.
  const callbacksRef = useRef({ onTransform, onRevert, capture });
  callbacksRef.current = { onTransform, onRevert, capture };

  // Drop the pending timer if the component goes away mid-debounce.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const cancelPending = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    cancelPending();
    setOriginal(null);
    // The text this revert applied to is gone, so the latch goes with it.
    setRevertedText(null);
    transformationIdRef.current = null;
    appliedRef.current = null;
  }, [cancelPending]);

  // Reports a rewrite the user has not been shown before, under a fresh id.
  const reportTransform = useCallback((typed: string, transformed: string) => {
    const id = `transform_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    transformationIdRef.current = id;
    callbacksRef.current.capture({
      event: "query_transformed",
      context: {
        // Pairs this event with the revert that may follow.
        transformationId: id,
        // What the user typed.
        rawQuery: typed,
        // What the box now shows.
        transformedQuery: transformed,
        // Marks this as the client-side fraction rewrite.
        transformationType: "fraction_auto_transform",
      },
    });
  }, []);

  /**
   * Brings the box into line with what the user has typed: applies the rewrite
   * their text now calls for, or puts their text back when an earlier
   * provisional rewrite no longer holds. Returns what the box ends up reading.
   *
   * Both the debounce and a committed search land here, so the two can't drift
   * apart; they differ only in whether a rewrite of the unfinished trailing
   * word is held back.
   */
  const settle = useCallback(
    (boxValue: string, holdUnfinishedWord: boolean): string => {
      const typed = typedTextFor(boxValue, appliedRef.current);

      // The user asked to keep this text, so leave it alone.
      const transformed = holdsRevertedText(typed, revertedText)
        ? null
        : detectFractionTransform(typed, holdUnfinishedWord);

      if (transformed !== null) {
        const applied = appliedRef.current;
        // Growing the query that produced the rewrite carries the same one
        // forward, so it keeps its id — re-reporting would double-count it.
        //
        // The box still has to be showing that rewrite, though. A user who
        // types the shorthand back over it is starting the exchange again,
        // not extending it: the rewrite returns, and so must the offer to
        // undo it, or they could never tell the box to leave their text
        // alone once the offer had expired.
        const continuing =
          applied !== null &&
          boxValue.startsWith(applied.transformed) &&
          typed.startsWith(applied.typed);

        appliedRef.current = { typed, transformed };

        // Searching retires the offer: the user has acted on the rewrite, and
        // a banner outliving the search it belonged to just follows them down
        // the page. The exception is a rewrite that lands on this very search
        // — the held-back trailing word applying at Enter — which the user is
        // seeing for the first time, and which gets its own offer until the
        // next search.
        const landedNow = boxValue !== transformed;
        setOriginal(holdUnfinishedWord || landedNow ? typed : null);
        if (landedNow) {
          callbacksRef.current.onTransform(transformed);
        }
        if (!continuing) reportTransform(typed, transformed);
        return transformed;
      }

      // Nothing applies, so withdraw any rewrite still standing in for the
      // typed text and take the banner down with it.
      appliedRef.current = null;
      transformationIdRef.current = null;
      setOriginal(null);
      if (boxValue !== typed) callbacksRef.current.onTransform(typed);
      return typed;
    },
    [revertedText, reportTransform],
  );

  const handleInputChange = useCallback(
    (newValue: string) => {
      // An emptied box starts over, transforms included. The prefix test in
      // holdsRevertedText can't see this on its own — "" is a prefix of every
      // reverted text — so the latch is dropped here.
      if (newValue.length === 0) {
        setRevertedText(null);
        appliedRef.current = null;
      }

      // Typing dismisses the banner; the settle below re-raises it if the new
      // text still transforms.
      setOriginal(null);

      cancelPending();
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        // Mid-typing, so a rewrite of the unfinished trailing word waits.
        settle(newValue, true);
      }, TRANSFORM_DEBOUNCE_MS);
    },
    [cancelPending, settle],
  );

  const commit = useCallback(
    (currentValue: string): string => {
      // The committed text is searched here and now, so nothing is left to
      // debounce, and the trailing word is finished by definition.
      cancelPending();
      return settle(currentValue, false);
    },
    [cancelPending, settle],
  );

  const revert = useCallback(() => {
    if (original === null) return;
    callbacksRef.current.capture({
      event: "query_transform_reverted",
      context: {
        // Matches the id sent with the transform being undone.
        transformationId: transformationIdRef.current ?? "unknown",
        // The fraction the box was showing.
        transformedQuery: appliedRef.current?.transformed ?? original,
        // The text going back in.
        revertedQuery: original,
      },
    });
    cancelPending();
    setRevertedText(original);
    setOriginal(null);
    transformationIdRef.current = null;
    appliedRef.current = null;
    callbacksRef.current.onRevert(original);
  }, [original, cancelPending]);

  return useMemo(
    () => ({
      original,
      handleInputChange,
      commit,
      revert,
      reset,
    }),
    [original, handleInputChange, commit, revert, reset],
  );
}
